import { z } from "zod"
import { runPreparationSummarySchema } from "./run-preparation"
import { marketplaceIdSchema, type MarketplaceId } from "../marketplace-id"
import {
  isoDate,
  jsonRecord,
  listingStatusSchema,
  masterCategorySchema,
  runOriginSchema,
} from "./primitives"

// ── ListingRun ─────────────────────────────────────────────────────────────

export const listingRunSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  marketplace: marketplaceIdSchema,
  sourceType: z.string(),
  sourceValue: z.string(),
  // Product master category this run targets (clothing / nutrition /
  // equipment). Operator-assignable and independent of how the run was sourced.
  // Null = unassigned.
  masterCategory: masterCategorySchema.nullable(),
  startedBy: z.string(),
  status: listingStatusSchema,
  sourceFilter: jsonRecord,
  sourceWalkKind: z.string().nullable().optional(),
  sourceWalkCategory: z.string().nullable().optional(),
  sourceWalkLimit: z.number().int().nullable().optional(),
  sourceWalkItemCount: z.number().int().nullable().optional(),
  categoryWalk: z
    .discriminatedUnion("status", [
      z.object({
        status: z.literal("verified"),
        category: masterCategorySchema,
        limit: z.number().int().positive(),
        itemCount: z.number().int().positive(),
        discoveryConfigSkus: z.array(z.string()),
        retainedExclusions: z.array(
          z.object({
            configSku: z.string(),
            simpleSkus: z.array(z.string()),
            reason: z.literal("delisted_out_of_stock"),
          })
        ),
      }),
      z.object({ status: z.literal("failed"), message: z.string() }),
    ])
    .nullable()
    .optional(),
  origin: runOriginSchema,
  automaticSourceEventCount: z.number().int().nonnegative(),
  automaticLatestEventId: z.number().int().positive().nullable(),
  automaticLatestSourceUpdatedAt: z.string().nullable(),
  automaticDirtyAt: z.string().nullable(),
  preparation: runPreparationSummarySchema.nullable().optional(),

  // Latest feed-build result. Null until first build.
  feedId: z.string().nullable(),
  feedHash: z.string().nullable(),
  feedFilename: z.string().nullable(),
  feedFormat: z.string().nullable(),
  feedArtifactId: z.number().int().nullable(),
  feedRowCount: z.number().int().nullable(),
  feedColumnCount: z.number().int().nullable(),
  droppedRowCount: z.number().int().nullable(),
  builtAt: z.string().nullable(),
  builtBy: z.string().nullable(),

  // Open feed gaps (build blockers) as of the last whole-run validation.
  // Null = never validated; 0 = build-ready. Surfaced as the run's "errors".
  feedGapCount: z.number().int().nullable(),

  // Marketplace-eligible variant ("simple") count as of the last validation.
  // Derived cache like feedGapCount — null = never validated. The run header
  // reads it so the count costs no live mirror read on every page open.
  simpleCount: z.number().int().nullable(),

  // Variant SKUs that are eligible to list but have no offer on the marketplace
  // yet, as of the last offer-snapshot pull. Derived cache like feedGapCount —
  // null = never pulled (no live read on the runs list); 0 = every eligible
  // variant already has an offer. The runs list + run header read this so the
  // count costs no live marketplace read per page open. Refreshed when the
  // feed-preview or validate sweep pulls the offer snapshot.
  offersNeededCount: z.number().int().nullable(),

  // NOK split used by list/header readiness. Detail computes it from current
  // run-item verdicts via `RunSkuSummary`; the list response derives the same
  // split for each row, and leaves it null while the run has no items or any
  // item verdict is still pending. Persisted DB values are only the
  // last-validation cache.
  // `nokFixable` = configs with a fixable gap (real work); `nokBlocked` = pure
  // eligibility won't-list.
  nokFixable: z.number().int().nullable(),
  nokBlocked: z.number().int().nullable(),

  // Operator approval (C-9).
  approvedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),

  // Latest upload result.
  marketplaceImportId: z.string().nullable(),
  marketplaceStatus: z.string().nullable(),
  marketplaceStatusObservedAt: z.string().nullable(),
  marketplaceImportNormalizedStatus: z.string().nullable(),
  marketplaceImportStage: z.enum(["transforming", "integrating", "unknown", "terminal"]).nullable(),
  marketplaceImportFirstObservedAt: z.string().nullable(),
  marketplaceImportNextPollAt: z.string().nullable(),
  marketplaceImportPollAttemptCount: z.number().int().nonnegative().nullable(),
  marketplaceImportPollErrorCount: z.number().int().nonnegative().nullable(),
  marketplaceImportPollLastError: z.string().nullable(),
  marketplaceImportPollLastErrorAt: z.string().nullable(),
  marketplaceImportLeaseHealth: z.enum(["idle", "leased", "expired"]).nullable(),
  marketplaceImportLeaseExpiresAt: z.string().nullable(),
  marketplaceImportUnresolvedAt: z.string().nullable(),
  marketplaceImportTerminalAt: z.string().nullable(),
  importedAt: z.string().nullable(),
  importedBy: z.string().nullable(),

  cancelledAt: z.string().nullable(),

  // Archive flag — orthogonal to status. Set when an operator shelves a dead
  // run; null for live runs. Reversible via unarchive.
  archivedAt: z.string().nullable(),
  archivedBy: z.string().nullable(),

  error: z.string().nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
})
export type ListingRun = z.infer<typeof listingRunSchema>

export const runStatusCountsSchema = z.record(listingStatusSchema, z.number().int().nonnegative())
export type RunStatusCounts = z.infer<typeof runStatusCountsSchema>
export function emptyRunStatusCounts(): RunStatusCounts {
  return { open: 0, importing: 0, imported_with_errors: 0, imported: 0, failed: 0, cancelled: 0 }
}
export const runsListResponseSchema = z.object({
  runs: z.array(listingRunSchema),
  statusCounts: runStatusCountsSchema,
})
export type RunsListResponse = z.infer<typeof runsListResponseSchema>

export const relistRunPolicySchema = z.object({
  marketplace: marketplaceIdSchema,
  name: z.string(),
  enabled: z.boolean(),
  revision: z.number().int().nonnegative(),
  executionEnabled: z.boolean(),
  effectiveEnabled: z.boolean(),
  activatedAt: z.string().nullable(),
  activationEventId: z.number().int().nonnegative().nullable(),
  lastSeenEventId: z.number().int().nonnegative().nullable(),
})
export type RelistRunPolicy = z.infer<typeof relistRunPolicySchema>
export const relistRunPoliciesSchema = z.object({ marketplaces: z.array(relistRunPolicySchema) })
export type RelistRunPolicies = z.infer<typeof relistRunPoliciesSchema>

export const setRelistRunPolicyBodySchema = z
  .object({
    enabled: z.boolean(),
    expectedRevision: z.number().int().nonnegative(),
  })
  .strict()

export const relistRunPolicyRevisionSchema = z.object({
  marketplace: marketplaceIdSchema,
  revision: z.number().int().positive(),
  beforeEnabled: z.boolean(),
  afterEnabled: z.boolean(),
  actor: z.string(),
  subject: z.string(),
  createdAt: z.string(),
})
export type RelistRunPolicyRevision = z.infer<typeof relistRunPolicyRevisionSchema>
export const relistRunPolicyHistorySchema = z.object({
  revisions: z.array(relistRunPolicyRevisionSchema),
  nextBeforeRevision: z.number().int().positive().nullable(),
})
export type RelistRunPolicyHistory = z.infer<typeof relistRunPolicyHistorySchema>
export const relistRunPolicyHistoryQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    beforeRevision: z.coerce.number().int().positive().optional(),
  })
  .strict()

export const relistRunMarketplaceInboxStatusSchema = relistRunPolicySchema.extend({
  lastEnrolledAt: z.string().nullable(),
  pending: z.number().int().nonnegative(),
  claimed: z.number().int().nonnegative(),
  enrolled: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
  blockers: z.array(z.object({ reason: z.string(), count: z.number().int().positive() })),
})
export type RelistRunMarketplaceInboxStatus = z.infer<typeof relistRunMarketplaceInboxStatusSchema>
export const relistRunInboxStatusSchema = z.object({
  marketplaces: z.array(relistRunMarketplaceInboxStatusSchema),
})
export type RelistRunInboxStatus = z.infer<typeof relistRunInboxStatusSchema>

export const createRunBodySchema = z
  .object({
    marketplace: marketplaceIdSchema,
    sourceType: z.string().min(1),
    sourceValue: z.string().min(1),
    sourceFilter: jsonRecord.optional(),
    // Defaults to `operator` server-side. `automatic` is internal-only and
    // cannot be requested through the public route. `qa` is system-gated: only the
    // `system:nova-qa` principal (or an admin) may request it; any other caller
    // is rejected. The QA cron passes `qa` so its runs stay off operator
    // surfaces and are reaped each tick.
    origin: runOriginSchema.optional(),
  })
  .strict()
export type CreateRunBody = z.infer<typeof createRunBodySchema>

export const createRunFromCategoryBodySchema = z
  .object({
    marketplace: marketplaceIdSchema,
    masterCategory: masterCategorySchema,
    limit: z.number().int().min(1).max(10000).optional(),
  })
  .strict()
export type CreateRunFromCategoryBody = z.infer<typeof createRunFromCategoryBodySchema>

export const createRunFromCategoryResponseSchema = z.object({
  run: z.object({ id: z.number().int(), slug: z.string() }),
  registered: z.array(z.object({ configSku: z.string() })),
  excluded: z.array(z.object({ sku: z.string(), reasons: z.array(z.string()) })),
})
export type CreateRunFromCategoryResponse = z.infer<typeof createRunFromCategoryResponseSchema>

export const cancelRunBodySchema = z
  .object({
    reason: z.string().optional(),
  })
  .strict()
export type CancelRunBody = z.infer<typeof cancelRunBodySchema>

export const failRunBodySchema = z
  .object({
    error: z.string().min(1),
  })
  .strict()
export type FailRunBody = z.infer<typeof failRunBodySchema>

// One durable item result posted by the delegated Codex worker. The API stores
// the model output before validation/apply, so a restart never requires another
// paid call for this item.
export const extractOperationResultBodySchema = z
  .object({
    claimId: z.string().uuid(),
    raw: z.unknown().optional(),
    error: z.string().optional(),
    modelUsed: z.string().min(1),
  })
  .refine((value) => value.raw !== undefined || value.error !== undefined, {
    message: "raw or error is required",
  })
  .strict()
export type ExtractOperationResultBody = z.infer<typeof extractOperationResultBodySchema>

// Body for POST /api/runs/:slug/extract/reapply — re-write each config's
// already-persisted extract payload into overrides WITHOUT a model call.
// Optional `skus` narrows the re-apply to a subset; omitted ⇒ every persisted
// extract on the run.
export const extractReapplyBodySchema = z
  .object({
    skus: z.array(z.string().min(1)).optional(),
  })
  .strict()
export type ExtractReapplyBody = z.infer<typeof extractReapplyBodySchema>

export type ProductValueApplicationReport = {
  configSku: string
  eventId: number
  status: "applied" | "pending" | "failed" | "stale"
}

export type ExtractReapplyReport = {
  reapplied: number
  skippedNoSuccess: number
  extracted: number
  extractedWithWarnings: number
  validationFailed: number
  extractError: number
  errors: { configSku: string; message: string }[]
  applications: ProductValueApplicationReport[]
}

// Response for POST /api/runs/:slug/extract — the one wire contract the API
// (which assembles it server-side on the gb-llm path) and the CLI (which
// assembles the same shape from the inputs/results round-trip on the codex
// path) both build against, so the operator sees an identical report either
// way. The buckets sum to `total`.
export type ExtractOperationItemStatus =
  | "queued"
  | "running"
  | "successful"
  | "validation_failed"
  | "extract_error"
  | "stale_input"

export type ExtractOperationSnapshot = {
  id: string
  runSlug: string
  marketplace: MarketplaceId
  provider: "codex" | "gb-llm"
  model: string
  status: "running" | "completed" | "completed_with_failures" | "interrupted"
  force: boolean
  createdAt: string
  updatedAt: string
  finishedAt: string | null
  total: number
  queued: number
  running: number
  successful: number
  validationFailed: number
  extractError: number
  staleInput: number
  skipped: number
  applicationCounts: { applied: number; pending: number; failed: number; stale: number }
  applicationFailures: {
    configSku: string
    eventId: number
    status: "failed" | "stale"
  }[]
  failures: {
    configSku: string
    status: Exclude<ExtractOperationItemStatus, "queued" | "running" | "successful">
    message: string
    applicationStatus: ProductValueApplicationReport["status"] | null
  }[]
}

export type ExtractOperationClaim = {
  item: {
    claimId: string
    configSku: string
    prompt: string
    jsonSchema: Record<string, unknown>
    model: string
  } | null
}

// ── Run-item verdict (OK / NOK) ─────────────────────────────────────────────
//
// Per-SKU verdict for one run, surfaced as the OK/NOK tabs on the run page.
// "ok" = on track to list; "nok" = won't list or not yet listable. The reasons
// split into two kinds so the NOK breakdown can separate "still work to do"
// from "correctly won't list":
//   - "eligibility" — a marketplace rule won't list this SKU at all (sub-price,
//     invalid EAN, unavailable, or a persisted historical bulk-pack verdict …).
//     Not fixable inside nova.
//   - "gap" — an open feed gap (no writer/category, failed extract, missing
//     required attribute). Fixable by iterating (author writer, classify,
//     re-extract), then re-validating.
// Mirrors `listing_run_items.verdict` / `verdict_reasons` in the DB schema.
export const runItemVerdictSchema = z.enum(["ok", "nok"])
export type RunItemVerdict = z.infer<typeof runItemVerdictSchema>

export const runItemVerdictReasonSchema = z.object({
  kind: z.enum(["eligibility", "gap"]),
  reason: z.string(),
  fixable: z.boolean(),
})
export type RunItemVerdictReason = z.infer<typeof runItemVerdictReasonSchema>

export const listingRunItemSchema = z.object({
  id: z.number().int(),
  runId: z.number().int(),
  marketplace: marketplaceIdSchema,
  configSku: z.string(),
  category: z.string().nullable(),
  // Null until first evaluated (eligibility seed at registration, or the
  // whole-run validate sweep). See `runItemVerdictSchema` above.
  verdict: runItemVerdictSchema.nullable(),
  verdictReasons: z.array(runItemVerdictReasonSchema).nullable(),
  verdictAt: z.string().nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
})
export type ListingRunItem = z.infer<typeof listingRunItemSchema>

// One NOK reason rolled up across a run: how many SKUs carry it, and whether
// it's fixable (gap) or won't-list (eligibility). Powers the run page's NOK
// reason chips without shipping every SKU's reason array to the client.
export const runVerdictReasonTallySchema = z.object({
  reason: z.string(),
  fixable: z.boolean(),
  count: z.number().int(),
})
export type RunVerdictReasonTally = z.infer<typeof runVerdictReasonTallySchema>

// The run summary's NOK reason histogram entry: the base tally PLUS a
// CONFIG-COHORT split. `fixableCount` = how many Fixable-cohort configs (the
// CARRYING config hasFixableGap) carry this reason; `excludedCount` = how many
// Excluded-cohort (pure won't-list) configs carry it. Bucketed by the config's
// cohort membership, NOT the reason's own `fixable` flag — so a won't-list
// reason sitting on a config that ALSO has a fixable gap counts under
// `fixableCount`, matching the `cohort=fixable & reason=…` grid instead of
// leaking into the Excluded facet. This is what keeps each cohort's facet count
// exactly equal to its cohort+reason grid (same hasFixableGap predicate on both
// sides). fixableCount + excludedCount === count; `fixable` stays the reason's
// intrinsic fixability, used only for the chip icon/tone.
export const runReasonCohortTallySchema = runVerdictReasonTallySchema.extend({
  fixableCount: z.number().int(),
  excludedCount: z.number().int(),
})
export type RunReasonCohortTally = z.infer<typeof runReasonCohortTallySchema>

// Server-computed verdict rollup for one run. Replaces shipping the full
// per-SKU list to the run page, which only needed these aggregates (config
// count + OK/NOK/pending tallies + the NOK reason histogram). Computed in SQL /
// a scoped read (see getRunSkuSummary), so the payload is O(1), not O(configs).
export const runSkuSummarySchema = z.object({
  total: z.number().int(),
  ok: z.number().int(),
  nok: z.number().int(),
  // SKUs not yet evaluated (verdict null until the whole-run validate sweep).
  pending: z.number().int(),
  // The NOK total split into the run page's three cohorts, EXCLUDING parked
  // (set-aside) configs so the three never double-count a parked row:
  // `nokFixable` (Fixable cohort) configs carry at least one fixable (gap)
  // reason — writer / category / attribute work; `nokBlocked` (Excluded cohort)
  // configs are pure won't-list (eligibility only). Together with `setAside`
  // they partition `nok`: nokFixable + nokBlocked + setAside === nok (a parked
  // config lands in `setAside`, never here — set-aside only parks fixable-gap =
  // NOK configs, so every parked row is a NOK row). `no_eligible_variants` is a
  // symptom, not a cause, so it never counts toward either split — its
  // fixability is whatever its underlying causes are, and those causes are
  // already separate reasons here.
  nokFixable: z.number().int(),
  nokBlocked: z.number().int(),
  // The Fixable-cohort configs that would flip to listable once their missing
  // data is filled: every real reason is fixable, so no hard won't-list blocker
  // remains (see isRecoverable). A subset of `nokFixable` — a config can be
  // fixable yet still won't-list when it ALSO carries an eligibility blocker.
  // Also excludes parked configs, like `nokFixable`.
  nokRecoverable: z.number().int(),
  // Configs an operator parked in the per-run "set aside" bucket (the third NOK
  // cohort). A set-aside config produces no feed row and is excluded from the
  // run's blocking gap count. Counts ONLY parked rows whose verdict is `nok`, so
  // it is genuinely "the parked slice of NOK": the three cohorts sum to `nok`
  // exactly (nokFixable + nokBlocked + setAside === nok) even if the CLI parks an
  // OK/pending config (that config produces no feed row but is not shown under
  // the NOK view's Set-aside sub-tab count).
  setAside: z.number().int(),
  // NOK reason histogram across the NON-parked NOK configs (the Fixable +
  // Excluded cohorts). Each entry carries a config-cohort split (fixableCount /
  // excludedCount) so the run page's Filters popover shows, per cohort, exactly
  // the number of configs the `cohort=… & reason=…` grid returns — a won't-list
  // reason on a config that also has a fixable gap counts under the Fixable
  // cohort, not the Excluded one. Parked rows are excluded (they are the third,
  // Set-aside cohort, which browses its own grid). See runReasonCohortTally.
  reasons: z.array(runReasonCohortTallySchema),
})
export type RunSkuSummary = z.infer<typeof runSkuSummarySchema>

export const runOutputWarningSchema = z.object({
  code: z.enum(["pim_nutrition_disabled"]),
  severity: z.enum(["warning"]),
  message: z.string(),
})
export type RunOutputWarning = z.infer<typeof runOutputWarningSchema>

export const runDetailResponseSchema = z.object({
  run: listingRunSchema,
  skuSummary: runSkuSummarySchema,
  /** Run-level warnings that affect how an agent should interpret gaps and
   *  header readiness signals. `pim_nutrition_disabled` hard-blocks a real
   *  build (422) and also refuses the WRITER (materialization declines to
   *  persist blanks while the resolver is down) — the fix is configuration,
   *  not per-SKU healing. */
  warnings: z.array(runOutputWarningSchema),
})
export type RunDetailResponse = z.infer<typeof runDetailResponseSchema>

// ── Run reconciliation (clarity / completeness audit) ────────────────────────
//
// Answers "is this run good?" — every drop bucketed legitimate-vs-suspect, plus
// the input-health signals (mirror freshness, silent truncation) that determine
// whether the run even saw the full catalog. See `listing-runs/reconcile.ts`.

export const storeFreshnessSchema = z.object({
  store: z.string(),
  // ISO timestamp of the most recent SUCCESSFUL catalog sync, null if never.
  lastSyncAt: z.string().nullable(),
  ageSeconds: z.number().int().nullable(),
  stale: z.boolean(),
})
export type StoreFreshnessInfo = z.infer<typeof storeFreshnessSchema>

// A NOK reason tally enriched with its reconciliation class: `legit` = a
// deliberate won't-list gate; `suspect` = a data/pipeline gap that may be hiding
// a product we should be listing. See `reasonClass` in verdict-reasons.ts.
export const reconcileReasonTallySchema = runVerdictReasonTallySchema.extend({
  class: z.enum(["legit", "suspect"]),
})
export type ReconcileReasonTally = z.infer<typeof reconcileReasonTallySchema>

export const runReconciliationSchema = z.object({
  slug: z.string(),
  marketplace: marketplaceIdSchema,
  masterCategory: z.string().nullable(),
  // The Magento store this run reads (null if the marketplace config is missing).
  store: z.string().nullable(),
  counts: z.object({
    total: z.number().int(),
    listed: z.number().int(), // verdict ok
    pending: z.number().int(), // not yet evaluated
    // NOK partitioned on the legit/suspect axis (legitExcluded + suspect === nok).
    legitExcluded: z.number().int(),
    suspect: z.number().int(),
  }),
  reasons: z.array(reconcileReasonTallySchema),
  // The configured page limit and whether the category likely overflowed it
  // (count === limit ⇒ products may have been silently dropped).
  limit: z.number().int().nullable(),
  truncated: z.boolean(),
  freshness: storeFreshnessSchema.nullable(),
})
export type RunReconciliation = z.infer<typeof runReconciliationSchema>
